import React from 'react';
import { InsertDriveFile } from '@material-ui/icons';
import imageCompression from 'browser-image-compression';
import PdfIcon from './assets/icons/svg/PdfIcon';
import VideoFileIcon from './assets/icons/svg/VideoFileIcon';
import WordFileIcon from './assets/icons/svg/WordFileIcon';
import ExcelFileIcon from './assets/icons/svg/ExcelFileIcon';
import { IAvailableColumnModel, IKSKDebtModel } from './interfaces';

export const isString = (variable: any): boolean => {
  return typeof variable === 'string';
};

export const numberMask = (value: string | null, mask: string): string => {
  if (!value) {
    return '';
  }

  let i = 0;
  let lastReplacedIndex = -1;
  const filledMask = mask?.replace(/#/g, (_, j) => {
    if (i >= value.length) {
      return '#';
    }
    lastReplacedIndex = j;
    return value[i++];
  });

  return filledMask?.substring(0, lastReplacedIndex + 1);
};

export const cleanNumberMask = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

export const formatTime = (seconds: number): string => {
  return [
    parseInt(JSON.stringify(seconds / 60 / 60)),
    parseInt(JSON.stringify((seconds / 60) % 60)),
    parseInt(JSON.stringify(seconds % 60)),
  ]
    .join(':')
    .replace(/\b(\d)\b/g, '0$1');
};

export const numberWithSpaces = (x: number | string): string => {
  if (typeof x === 'number') {
    x = x.toString();
  }

  x = x.toString().replace(/ /g, '');

  let parts = x.toString().split('.');

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return parts.join('.');
};

export interface IKskDebtMonth {
  /** Порядковый номер месяца (год * 12 + месяц) — для сортировки. */
  month: number;
  endSumma: number;
  added: number;
}

/**
 * Начисления по услугам сервисного обслуживания (ответ getDebtKSK), сгруппированные
 * по месяцу и отсортированные от старых к свежим.
 */
export const getKskDebtByMonth = (debt: IKSKDebtModel | null): IKskDebtMonth[] => {
  const informationOfDebt = debt?.informationOfDebt;
  if (!informationOfDebt?.length) {
    return [];
  }

  const byMonth = new Map<number, IKskDebtMonth>();
  informationOfDebt.forEach((item) => {
    const date = new Date(item.month);
    // ключ месяца — числом, чтобы сортировка не путала сентябрь и октябрь одного года
    const month = date.getFullYear() * 12 + date.getMonth();
    const current = byMonth.get(month) || { month, endSumma: 0, added: 0 };
    current.endSumma += item.endSumma;
    current.added += item.added;
    byMonth.set(month, current);
  });

  return Array.from(byMonth.values()).sort((a, b) => a.month - b.month);
};

/**
 * Сумма задолженности (ДЗ) — итог последнего месяца начислений. Та же сумма,
 * что показывается в предупреждении о задолженности при создании заявки.
 */
export const getKskDebtTotal = (debt: IKSKDebtModel | null): number => {
  const months = getKskDebtByMonth(debt);
  const [lastMonth] = months.slice(-1);
  return lastMonth ? lastMonth.endSumma : 0;
};

export const clearPhoneNumberFormat = (phone: string | null, prefix: string): string => {
  if (!phone) {
    return '';
  }

  if (phone.startsWith(prefix)) phone = phone.slice(prefix?.length);

  return cleanNumberMask(phone);
};

export const getFileBytes = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(reader.result);
    };

    reader.readAsBinaryString(file);
  });
};

export const randomRgba = () => {
  var letters = '0123456789ABCDEF';
  var color = '#';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const APPLICATION_STATUSES: { [key: string]: [number, string] } = {
  NEW: [1, 'Новая'],
  IN_WORK: [2, 'В работе'],
  COMPLETED: [3, 'Выполнена'],
  REJECTED: [4, 'Отклонено'],
  DELAY: [5, 'В ожидании'],
  CLOSED: [6, 'Закрыта'],
  REJECTED_BY_CLIENT: [8, 'Отменена клиентом'],
  RE_OPENED: [9, 'Отрыта заново'],
  MOVED: [20, 'Перенесено'],
};

export const DEFECT_ACT_STATUSES: {
  [key: string]: [number, string];
} = {
  CREATED: [1, 'Создан дефектный акт'],
  NOTIFICATION_CREATED: [2, 'Создано уведомление'],
  NOTIFICATION_SENT: [3, 'Уведомление отправлено'],
  CONTRACTOR_DO: [4, 'Устраняет подрядчик'],
  NEEDS_CALCULATION: [5, 'Требует калькуляции'],
  CALCULATING: [6, 'На калькуляции'],
  CALCULATED: [7, 'Посчитан ПТО'],
  SENT_TO_1C: [8, 'Отправлен в 1с'],
  CLOSED: [9, 'Закрыт без ресурсов'],
  SENT_TO_PTO: [10, 'Отправлено в ПТО'],
  APPROVED: [11, 'Утверждено'],
  REJECTED: [12, 'Отклонено'],
  SUPPLEMENTED: [13, 'Дополнен'],
  ON_SIGNING: [14, 'На подписании'],
  SIGNED_BY_RECIPIENT: [15, 'Подписан получателем'],
  SIGNED_BY_SENDER: [16, 'Подписан отправителем'],
  MUTUALLY_SIGNED: [17, 'Подписан обоюдно'],
  BLOCKED: [18, 'Заблокирован'],
  UNLOCKED: [19, 'Разблокирован'],
  ELIMINATED_BY_THE_CONTRACTOR: [20, 'Устраняется подрядчиком'],
  DA_PRINTED: [21, 'Дефектный акт распечатан'],
  DA_EXTENDED_PRINTED: [22, 'Дефектный акт дополнен и распечатан'],
  NOTIFICATION_UPDATED: [100, 'Уведомление обновлено'],
};

export const APPLICATION_HISTORY = {
  CHANGE_STATUS: 1,
  COMMENT: 2,
  DELAY_DEADLINE: 5,
  CHANGE_EXECUTOR: 6,
  CREATED_APPLICATION: 7,
  DEF_ACT_CREATE: 20,
  DEF_ACT_UPDATED: 21,
  DEF_ACT_NOTIFICATION_CREATE: 22,
  DEF_ACT_NOTIFICATION_UPDATED: 23,
  DEF_ACT_NOTIFICATION_SENT: 24,
  DEF_ACT_RESULT_CREATED: 25,
  DEF_ACT_WORK_CREATED: 26,
  DEF_ACT_WORK_UPDATED: 27,
  DEF_ACT_CALCULATED: 28,
  DEF_ACT_CALCULATE_CHANGED: 29,
  DEF_ACT_SENT_ONE_SYSTEM: 30,
  DEF_ACT_RESULT_NOT_AGREE: 31,
  DEF_ACT_RESULT_NOT_COMING: 32,
  DEF_ACT_CLOSED_NO_COST: 33,
  DEF_ACT_NOTIFICATION_DELETED: 34,
  DEF_ACT_RESULT_AGREE_IS_FUND: 35,
  DEF_ACT_RESULT_AGREE_BY_CONTRACTOR: 36,
  DEF_ACT_SENT_TO_PTO: 37,
  DEF_ACT_NOW_WARRANTY: 38,
  DEF_ACT_CALCULATE_EXTENDED: 39,
  CHANGE_APPLICATION: 40,
  DEF_ACT_SENT_TO_PARTNERS: 41,
  DEF_ACT_RECEIVER_READ: 42,
  DEF_ACT_SENDER_READ: 43,
  DEF_ACT_RECEIVER_SIGNED: 44,
  DEF_ACT_SENDER_SIGNED: 45,
  DEF_ACT_SIGNED: 46,
  DEF_ACT_UNLOCKED: 47,
  DEF_ACT_BLOCKED: 48,
  NOTIFICATION_HAS_BEEN_READ: 49,
  CALLBACK_COMMENT: 50,
  BI_CLIENTS_RATING: 51,
  PAYMENT_ADDED: 52,
  CONCLUSION_ACT_CREATED: 55,
  CONCLUSION_ACT_UPDATED: 56,
  CONCLUSION_ACT_SENT_FOR_SIGNING: 57,
  CONCLUSION_ACT_SIGNED_BY_MEMBER: 58,
  CONCLUSION_ACT_REJECTED_BY_MEMBER: 59,
  CONCLUSION_ACT_SIGNED_BY_ALL_MEMBERS: 60,
  CONCLUSION_ACT_REVOKED: 61,
  DEF_ACT_TECH_DEP_ASSIGNED: 62,
  DEF_ACT_TECH_DEP_REJECTED: 63,
  DEF_ACT_TECH_DEP_COMPLETED: 64,
  DEF_ACT_TECH_DEP_ADDED_TO_MAILING: 65,
  CONTRAGENT_BAD_ADDED: 66,
  CONTRAGENT_BAD_DELETED: 67,
  EXECUTOR_APPOINTED: 1002,
  MARKED_AS_PUBLIC: 1003,
  TOGGLE_CHECKED: 1004,
  SAVE_APPLICATION_QUESTIONABLE: 1005,
  REMOVE_APPLICATION_QUESTIONABLE: 1006,
  APP_DELAY_CLIENT_SEND: 1007,
  APP_DELAY_CLIENT_REMOVE: 1008,
  APP_DELAY_CLIENT_ACCEPT: 1009,
  APP_DELAY_CLIENT_REJECT: 1010,
  APP_DELAY_CLIENT: 1011,
  APP_DELAY_CLIENT_AUTO_ACCEPT: 1012,
  CHANGE_CAPEX_PROGRESS: 1013,
  MARK_AND_COMMENT_FROM_EXECUTOR: 1014,
  CHANGE_USO_APP_STATUS: 1015,
  DEF_ACT_IS_MOP_NOTIFICATION_CREATED: 1016,
  DEF_ACT_IS_MOP_NOTIFICATION_UPDATED: 1017,
  NEW_APPLICATION_CREATED: 1018,
  CHECKLIST_FILLED: 1019,
  NOTIFICATION_SENT_TO_MANAGER: 1020,
  CHECKLIST_CREATED: 1021,
  DEF_ACT_PRE_NOTIFICATION_CREATE: 1022,
  DEF_ACT_PRE_NOTIFICATION_UPDATED: 1023,
  DEF_ACT_PRE_NOTIFICATION_SENT: 1024,
  DEF_ACT_RESULT_FIX_BY_BUILDER: 1025,
  DEF_ACT_TMC_SENT_TO_SUPPLIER: 1026,
  DEF_ACT_TMC_SUPPLIER_COMPLETED: 1027,
  DEF_ACT_TMC_SENT_TO_WRITE_OFF: 1028,
  SURVEY_ACT_CREATED: 1029,
  SURVEY_ACT_UPDATED: 1030,
  SURVEY_ACT_SENT_FOR_SIGNING: 1031,
  SURVEY_ACT_SIGNED_BY_MEMBER: 1032,
  SURVEY_ACT_REJECTED_BY_MEMBER: 1033,
  SURVEY_ACT_SIGNED_BY_ALL_MEMBERS: 1034,
  SURVEY_ACT_REVOKED: 1035,
  APP_OVERDUE: 1036,
  OBSERVER_ADDED: 1037,
  OBSERVER_REMOVED: 1038,
  OBSERVER_REMOVED_AS_EXECUTOR: 1039,
  OBSERVER_ADDED_BY_MANAGER: 1040,
  OBSERVER_REMOVED_BY_MANAGER: 1041,
};

export const USER_OPERATIONS = {
  ADD_APPLICATION_QUESTIONABLE: 'ADD_APPLICATION_QUESTIONABLE',
  ADMIN: 'ADMIN',
  AI_VIEW: 'AI_VIEW',
  APP_CHANGE_EXECUTOR: 'APP_CHANGE_EXECUTOR',
  APP_CHANGE_START_DATE: 'APP_CHANGE_START_DATE',
  APP_CLOSE: 'APP_CLOSE',
  APP_CREATE_FORUM: 'APP_CREATE_FORUM',
  APP_DELAY: 'APP_DELAY',
  APP_DELAY_CLIENT: 'APP_DELAY_CLIENT',
  APP_DELAY_REMOVE_CLIENT: 'APP_DELAY_REMOVE_CLIENT',
  APP_DONE: 'APP_DONE',
  APP_EDIT_IS_PUBLIC: 'APP_EDIT_IS_PUBLIC',
  APP_IN_PROGRESS: 'APP_IN_PROGRESS',
  APP_OBSERVE: 'APP_OBSERVE',
  APP_OBSERVER_MANAGE: 'APP_OBSERVER_MANAGE',
  APP_REJECT_BY_CLIENTS: 'APP_REJECT_BY_CLIENTS',
  APP_REOPEN: 'APP_REOPEN',
  APP_SAVE: 'APP_SAVE',
  APP_SAVE_COMMENT: 'APP_SAVE_COMMENT',
  APP_SETTINGS_MANAGER: 'APP_SETTINGS_MANAGER',
  APP_TAKE: 'APP_TAKE',
  APP_UPDATE_DEFECT_TYPE: 'APP_UPDATE_DEFECT_TYPE',
  APP_UPDATE_FULL: 'APP_UPDATE_FULL',
  APP_WAIT: 'APP_WAIT',
  APPLICATION_ACCIDENT: 'APPLICATION_ACCIDENT',
  APPLICATION_CHECK_LIST: 'APPLICATION_CHECK_LIST',
  APPLICATION_DELAY_REPORT_VIEW: 'APPLICATION_DELAY_REPORT_VIEW',
  APPLICATION_GROUP_REPORT_VIEW: 'APPLICATION_GROUP_REPORT_VIEW',
  APPLICATION_GROUP_VIEW: 'APPLICATION_GROUP_VIEW',
  APPLICATION_HISTORY: 'APPLICATION_HISTORY',
  APPLICATION_LIST_EXPORT: 'APPLICATION_LIST_EXPORT',
  APPLICATION_LIST_SHORT: 'APPLICATION_LIST_SHORT',
  APPLICATION_PURCHASE: 'APPLICATION_PURCHASE',
  APPLICATION_PURCHASE_VIEW: 'APPLICATION_PURCHASE_VIEW',
  APPLICATION_VIEW: 'APPLICATION_VIEW',
  CALCULATOR_TARIFF_CHECK_LIST: 'CALCULATOR_TARIFF_CHECK_LIST',
  CALCULATOR_TARIFF_DICT_MANAGER: 'CALCULATOR_TARIFF_DICT_MANAGER',
  CALCULATOR_TARIFF_FIN_BLOCK: 'CALCULATOR_TARIFF_FIN_BLOCK',
  CALCULATOR_TARIFF_FIN_DIRECTOR: 'CALCULATOR_TARIFF_FIN_DIRECTOR',
  CALCULATOR_TARIFF_FIN_MANAGER: 'CALCULATOR_TARIFF_FIN_MANAGER',
  CALCULATOR_TARIFF_PARKING_BY_COUNT: 'CALCULATOR_TARIFF_PARKING_BY_COUNT',
  CALLCENTER_CALLBACK_OPERATION: 'CALLCENTER_CALLBACK_OPERATION',
  CAPEX_MANAGER: 'CAPEX_MANAGER',
  CAPEX_VIEW: 'CAPEX_VIEW',
  CHECK_LIST_AFTER_MANGER: 'CHECK_LIST_AFTER_MANGER',
  CHECK_LIST_APPLICATION_EDIT: 'CHECK_LIST_APPLICATION_EDIT',
  CHECKLIST_ADD_FOR_REPORT: 'CHECKLIST_ADD_FOR_REPORT',
  CHECKLIST_SEND_REPORT: 'CHECKLIST_SEND_REPORT',
  CHECKLIST_VIEW: 'CHECKLIST_VIEW',
  CHRONA_ADMIN: 'CHRONA_ADMIN',
  CHRONA_VIEW: 'CHRONA_VIEW',
  CHRONA_MANAGER_FIX: 'CHRONA_MANAGER_FIX',
  // Чтение объектов и назначений; все ЖК без объектного скоупа - у «просмотра по всем ЖК» и админа модуля.
  CHRONA_OBJECT_VIEW: 'CHRONA_OBJECT_VIEW',
  CHRONA_AUDIT_VIEW: 'CHRONA_AUDIT_VIEW',
  // Показ пунктов меню — отдельно от прав на данные: роли CHRONA_*_VIEW заведены ровно
  // под кнопки, а доступ к данным проверяет сервер.
  CHRONA_ADMIN_MENU: 'CHRONA_ADMIN_MENU',
  CHRONA_OBJECT_EDIT: 'CHRONA_OBJECT_EDIT',
  CHRONA_VISITS_MENU: 'CHRONA_VISITS_MENU',
  CHRONA_REVIEW_MENU: 'CHRONA_REVIEW_MENU',
  CLASSIFIER_DICTIONARY_EDIT: 'CLASSIFIER_DICTIONARY_EDIT',
  CONFLUENCE_LESSON_MANAGER: 'CONFLUENCE_LESSON_MANAGER',
  CONFLUENCE_MANAGER: 'CONFLUENCE_MANAGER',
  CONFLUENCE_VIEW: 'CONFLUENCE_VIEW',
  CONTACTS_OPERATION: 'CONTACTS_OPERATION',
  CONTRACTOR_REPORT_OPERATION: 'CONTRACTOR_REPORT_OPERATION',
  CONTRAGENT_BAD_EDIT: 'CONTRAGENT_BAD_EDIT',
  CREATE_APPLICATION_BASE_ON_APPLICATION: 'CREATE_APPLICATION_BASE_ON_APPLICATION',
  CRITERIA_RATING_UGO_EDIT: 'CRITERIA_RATING_UGO_EDIT',
  DEF_ACT_APPROVE: 'DEF_ACT_APPROVE',
  DEF_ACT_ASSIGN_TO_TECH_DEP: 'DEF_ACT_ASSIGN_TO_TECH_DEP',
  DEF_ACT_CLOSE_NO_COST: 'DEF_ACT_CLOSE_NO_COST',
  DEF_ACT_CONSIDERATION_BY_TECH_DEP: 'DEF_ACT_CONSIDERATION_BY_TECH_DEP',
  DEF_ACT_MEMBER_EDIT: 'DEF_ACT_MEMBER_EDIT',
  DEF_ACT_REJECT: 'DEF_ACT_REJECT',
  DEF_ACT_SAVE_CALCULATION: 'DEF_ACT_SAVE_CALCULATION',
  DEF_ACT_SAVE_DEF_ACT: 'DEF_ACT_SAVE_DEF_ACT',
  DEF_ACT_SAVE_DEF_ACT_FILE: 'DEF_ACT_SAVE_DEF_ACT_FILE',
  DEF_ACT_SAVE_NOTIFICATION: 'DEF_ACT_SAVE_NOTIFICATION',
  DEF_ACT_SAVE_RESULT: 'DEF_ACT_SAVE_RESULT',
  DEF_ACT_SAVE_WORK: 'DEF_ACT_SAVE_WORK',
  DEF_ACT_SEND_NOTIFICATION: 'DEF_ACT_SEND_NOTIFICATION',
  DEF_ACT_SENT_TO_ONE_SYSTEM: 'DEF_ACT_SENT_TO_ONE_SYSTEM',
  DEF_ACT_SENT_TO_PARTNERS: 'DEF_ACT_SENT_TO_PARTNERS',
  DEFECT_ACT_DIVISION_EDITOR: 'DEFECT_ACT_DIVISION_EDITOR',
  DEFECT_ACT_HISTORY: 'DEFECT_ACT_HISTORY',
  DEFECT_ACT_REASON_OPERATION: 'DEFECT_ACT_REASON_OPERATION',
  DEFECT_TYPE_EDIT: 'DEFECT_TYPE_EDIT',
  DELETE_AREA: 'DELETE_AREA',
  DELETE_AREA_EXECUTOR: 'DELETE_AREA_EXECUTOR',
  DELETE_AREA_HEAD: 'DELETE_AREA_HEAD',
  DELETE_AREA_REAL_ESTATE: 'DELETE_AREA_REAL_ESTATE',
  DELETE_REAL_ESTATE_EXECUTOR: 'DELETE_REAL_ESTATE_EXECUTOR',
  DELETE_SERVICE_ORG_HEAD: 'DELETE_SERVICE_ORG_HEAD',
  DELETE_STAFF: 'DELETE_STAFF',
  DICTIONARY_EQUIPMENT_MANAGER: 'DICTIONARY_EQUIPMENT_MANAGER',
  DICTIONARY_EQUIPMENT_TYPE_MANAGER: 'DICTIONARY_EQUIPMENT_TYPE_MANAGER',
  DICTIONARY_WORK_MANAGER: 'DICTIONARY_WORK_MANAGER',
  CLIENT_CARD_INFO: 'CLIENT_CARD_INFO',
  CLIENT_PROFILE_AI: 'CLIENT_PROFILE_AI',
  CLIENT_PROFILE_AI_MANAGER: 'CLIENT_PROFILE_AI_MANAGER',
  DISPLAY_APP_CLIENT_INFO: 'DISPLAY_APP_CLIENT_INFO',
  DISPLAY_CLIENT_SENSITIVE_INFO: 'DISPLAY_CLIENT_SENSITIVE_INFO',
  DOWNLOAD_RECORD: 'DOWNLOAD_RECORD',
  EMPLOYEE_MANAGER: 'EMPLOYEE_MANAGER',
  EVENT_EDITOR_OPERATION: 'EVENT_EDITOR_OPERATION',
  EXTRA_ROLE_OPERATION: 'EXTRA_ROLE_OPERATION',
  GPR_RUN_ROLLBACK: 'GPR_RUN_ROLLBACK',
  HIERARCHY_POSITION: 'HIERARCHY_POSITION',
  JOURNAL_EQUIPMENT_ITEM_STATUS: 'JOURNAL_EQUIPMENT_ITEM_STATUS',
  JOURNAL_EQUIPMENT_LIST_VIEW: 'JOURNAL_EQUIPMENT_LIST_VIEW',
  KPI_APPLICATION_MANAGER: 'KPI_APPLICATION_MANAGER',
  KPI_EDIT_INDICATOR: 'KPI_EDIT_INDICATOR',
  KPI_USER_VIEW: 'KPI_USER_VIEW',
  MAIL_EDITOR_OPERATION: 'MAIL_EDITOR_OPERATION',
  MANAGE_BIG_APP_MARKETPLACE: 'MANAGE_BIG_APP_MARKETPLACE',
  MANAGE_BIG_APP_NEWS: 'MANAGE_BIG_APP_NEWS',
  MANAGE_BIG_APP_POLLS: 'MANAGE_BIG_APP_POLLS',
  MANAGE_BIG_APP_WARRANTY_CARD: 'MANAGE_BIG_APP_WARRANTY_CARD',
  MANAGE_CONSULTATION_TYPE: 'MANAGE_CONSULTATION_TYPE',
  MANAGE_DELAY_REASON: 'MANAGE_DELAY_REASON',
  MANAGE_DONE_RESPONSE: 'MANAGE_DONE_RESPONSE',
  MANAGEMENT_UGO: 'MANAGEMENT_UGO',
  MANAGER_CHECK_LIST_AFTER_INDICATOR: 'MANAGER_CHECK_LIST_AFTER_INDICATOR',
  MANAGER_CHECK_LIST_BEFORE_INDICATOR: 'MANAGER_CHECK_LIST_BEFORE_INDICATOR',
  MANAGER_DEF_ACT_UPDATE: 'MANAGER_DEF_ACT_UPDATE',
  MANAGER_DEFECT_ACT_BRAND: 'MANAGER_DEFECT_ACT_BRAND',
  MANAGER_DEFECT_ACT_WORK_GUARANTEE: 'MANAGER_DEFECT_ACT_WORK_GUARANTEE',
  MANAGER_DICTIONARY_EQUIPMENT: 'MANAGER_DICTIONARY_EQUIPMENT',
  MANAGER_DICTIONARY_EQUIPMENT_TYPE: 'MANAGER_DICTIONARY_EQUIPMENT_TYPE',
  MANAGER_DIVISION: 'MANAGER_DIVISION',
  MANAGER_SELF_MANAGED_TEAM: 'MANAGER_SELF_MANAGED_TEAM',
  MANAGER_REAL_ESTATE_OBJECT_TYPE: 'MANAGER_REAL_ESTATE_OBJECT_TYPE',
  MANAGER_SERVICE_ORG: 'MANAGER_SERVICE_ORG',
  MANAGER_TECH_ROOM: 'MANAGER_TECH_ROOM',
  MODIFY_CALENDAR: 'MODIFY_CALENDAR',
  OKTELL_USER_EDIT: 'OKTELL_USER_EDIT',
  OPERATION_OBJECT_MANAGER: 'OPERATION_OBJECT_MANAGER',
  ORK_ACT_VIEW: 'ORK_ACT_VIEW',
  ORK_CHECKLIST_EDIT: 'ORK_CHECKLIST_EDIT',
  OVKS_VIEW: 'OVKS_VIEW',
  PARTNER_RATING_REPORT: 'PARTNER_RATING_REPORT',
  PARTNER_RATING_UGO_VIEW: 'PARTNER_RATING_UGO_VIEW',
  PERSONNEL_TRAINING_DICTIONARY_EDIT: 'PERSONNEL_TRAINING_DICTIONARY_EDIT',
  PERSONAL_BLACK_LIST: 'PERSONAL_BLACK_LIST',
  PERSONAL_REAL_ESTATE: 'PERSONAL_REAL_ESTATE',
  PERSONAL_REGISTRY_VIEW: 'PERSONAL_REGISTRY_VIEW',
  POSITION_ADMIN_OPERATION: 'POSITION_ADMIN_OPERATION',
  PPR_APPLICATION_VIEW: 'PPR_APPLICATION_VIEW',
  PPR_OBJECT: 'PPR_OBJECT',
  PPR_TASK_REPORT_VIEW: 'PPR_TASK_REPORT_VIEW',
  PPR_TASK_VIEW: 'PPR_TASK_VIEW',
  PROGRESS_CAPEX_MANAGER: 'PROGRESS_CAPEX_MANAGER',
  REAL_ESTATE_EVENT_OPERATION: 'REAL_ESTATE_EVENT_OPERATION',
  REAL_ESTATE_MANAGER_OPERATION: 'REAL_ESTATE_MANAGER_OPERATION',
  REAL_ESTATE_PARTNER_RATING_MODAL: 'REAL_ESTATE_PARTNER_RATING_MODAL',
  REAL_ESTATE_REPORT_OPERATION: 'REAL_ESTATE_REPORT_OPERATION',
  REAL_ESTATE_LEGAL_EDIT: 'REAL_ESTATE_LEGAL_EDIT',
  REAL_ESTATE_STAFF_SCHEDULE_EDIT: 'REAL_ESTATE_STAFF_SCHEDULE_EDIT',
  REASON_USO_MANAGER: 'REASON_USO_MANAGER',
  REASSIGN_APPS_OPERATION: 'REASSIGN_APPS_OPERATION',
  REASSIGN_EXECUTOR_APPS_OPERATION: 'REASSIGN_EXECUTOR_APPS_OPERATION',
  REMOVE_APPLICATION_QUESTIONABLE: 'REMOVE_APPLICATION_QUESTIONABLE',
  REMOVE_REAL_ESTATE_EXECUTOR_KEEP_APPS: 'REMOVE_REAL_ESTATE_EXECUTOR_KEEP_APPS',
  REOPEN_REASON_OPERATION: 'REOPEN_REASON_OPERATION',
  REPAIR_TYPE_OPERATION: 'REPAIR_TYPE_OPERATION',
  ROOM_TENANT: 'ROOM_TENANT',
  SAVE_AREA: 'SAVE_AREA',
  SAVE_AREA_EXECUTOR: 'SAVE_AREA_EXECUTOR',
  SAVE_AREA_HEAD: 'SAVE_AREA_HEAD',
  SAVE_AREA_REAL_ESTATE: 'SAVE_AREA_REAL_ESTATE',
  SAVE_REAL_ESTATE_EXECUTOR: 'SAVE_REAL_ESTATE_EXECUTOR',
  SAVE_SERVICE_ORG_HEAD: 'SAVE_SERVICE_ORG_HEAD',
  SAVE_STAFF: 'SAVE_STAFF',
  SEND_BIG_APP_LINK: 'SEND_BIG_APP_LINK',
  SEND_PAY_USO_LINK: 'SEND_PAY_USO_LINK',
  SERVICE_DICTIONARY_EDITOR_OPERATION: 'SERVICE_DICTIONARY_EDITOR_OPERATION',
  SERVICE_DICTIONARY_VIEW_OPERATION: 'SERVICE_DICTIONARY_VIEW_OPERATION',
  SERVICE_ORG_EDIT: 'SERVICE_ORG_EDIT',
  STANDARD_BI_SERVICE: 'STANDARD_BI_SERVICE',
  STANDARD_BI_SERVICE_WIKI: 'STANDARD_BI_SERVICE_WIKI',
  STANDARD_BIOT: 'STANDARD_BIOT',
  STANDARD_K2: 'STANDARD_K2',
  STANDARD_K2_OGO: 'STANDARD_K2_OGO',
  STANDARD_NOVA_CITY: 'STANDARD_NOVA_CITY',
  STATUS_REPORT: 'STATUS_REPORT',
  SURVEY_OPERATION: 'SURVEY_OPERATION',
  TAKE_A_CALL: 'TAKE_A_CALL',
  TECH_MAP_MANAGER: 'TECH_MAP_MANAGER',
  TECH_MAP_MANAGER_ENGINEER: 'TECH_MAP_MANAGER_ENGINEER',
  TECH_MAP_MANAGER_VIEW: 'TECH_MAP_MANAGER_VIEW',
  TENANT_VIEW_OPERATION: 'TENANT_VIEW_OPERATION',
  TOGGLE_CHECKED: 'TOGGLE_CHECKED',
  UPDATE_AREA: 'UPDATE_AREA',
  USER_KEYCLOAK_ADMIN: 'USER_KEYCLOAK_ADMIN',
  USO_REJECTED: 'USO_REJECTED',
  USO_REVOKE: 'USO_REVOKE',
  USO_SEND_SIGNED: 'USO_SEND_SIGNED',
  VIEW_APP_IS_PUBLIC_OPERATION: 'VIEW_APP_IS_PUBLIC_OPERATION',
  VIEW_APP_REGISTRY_DISABLED_OPERATION: 'VIEW_APP_REGISTRY_DISABLED_OPERATION',
  VIEW_AREAS: 'VIEW_AREAS',
  VIEW_AUDIT_LOG: 'VIEW_AUDIT_LOG',
  VIEW_CALENDAR: 'VIEW_CALENDAR',
  VIEW_CONCLUSION_ACT: 'VIEW_CONCLUSION_ACT',
  VIEW_FINE_INFO: 'VIEW_FINE_INFO',
  VIEW_POLL_AND_EVENT: 'VIEW_POLL_AND_EVENT',
  VIEW_REAL_ESTATES: 'VIEW_REAL_ESTATES',
  VIEW_REPORTS: 'VIEW_REPORTS',
  VIEW_REPORTS_MO: 'VIEW_REPORTS_MO',
  VIEW_SERVICE_ORG: 'VIEW_SERVICE_ORG',
  WORK_TYPE_MANAGER: 'WORK_TYPE_MANAGER',
};

export const APPLICATION_DEFAULT_COLUMNS: IAvailableColumnModel[] = [
  {
    columnName: 'createTimestamp',
    caption: 'Дата и время создания заявки',
    filterCaption: 'Дата и время создания заявки',
    type: 'Timestamp',
    filterCriteriaList: [],
    dictionaryMap: null,
  },
  {
    columnName: 'executorUserName',
    caption: 'Исполнитель заявки',
    filterCaption: 'Исполнитель заявки',
    type: 'String',
    filterCriteriaList: [],
    dictionaryMap: null,
  },
  {
    columnName: 'realEstateName',
    caption: 'Объект недвижимости',
    filterCaption: 'Объект недвижимости',
    type: 'String',
    filterCriteriaList: [],
    dictionaryMap: null,
  },
  {
    columnName: 'roomName',
    caption: 'Помещение',
    filterCaption: 'Помещение',
    type: 'String',
    filterCriteriaList: ['EQ'],
    dictionaryMap: null,
  },
  {
    columnName: 'defectName',
    caption: 'Тип замечаний',
    filterCaption: 'Тип замечаний',
    type: 'String',
    filterCriteriaList: [],
    dictionaryMap: null,
  },
  {
    columnName: 'statusName',
    caption: 'Статус',
    filterCaption: 'Статус',
    type: 'String',
    filterCriteriaList: [],
    dictionaryMap: null,
  },
  {
    columnName: 'contactName',
    caption: 'Контактное лицо',
    filterCaption: 'Контактное лицо',
    type: 'String',
    filterCriteriaList: ['CONTAINS_IGNORE_CASE'],
    dictionaryMap: null,
  },
  {
    columnName: 'contactPhone',
    caption: 'Тел. контактного лица',
    filterCaption: 'Тел. контактного лица',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: null,
  },
  {
    columnName: 'isPublic',
    caption: 'Публичная',
    filterCaption: 'Публичная',
    type: 'Boolean',
    filterCriteriaList: ['EQ'],
    dictionaryMap: null,
  },
];

export const APPLICATION_DEFAULT_FILTERS: IAvailableColumnModel[] = [
  {
    columnName: 'applicationNumber',
    caption: 'Номер заявки',
    filterCaption: 'Номер заявки',
    type: 'String',
    filterCriteriaList: ['EQ'],
    dictionaryMap: null,
  },
  {
    columnName: 'realEstateGuid',
    caption: 'ИД Объекта недвижимости',
    filterCaption: 'Объект недвижимости',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/dictionary/realEstatesAllLimit20',
      keyColumnName: 'guid',
      valueColumnName: 'name',
      searchColumn: 'name',
    },
  },
  {
    columnName: 'executorId',
    caption: 'ИД исполнителя заявки',
    filterCaption: 'Исполнитель заявки',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/users/listPerformance',
      keyColumnName: 'username',
      valueColumnName: 'firstLastName',
      searchColumn: 'search',
    },
  },
  {
    columnName: 'createDate',
    caption: 'Дата создания',
    filterCaption: 'Дата создания',
    type: 'Date',
    filterCriteriaList: ['BETWEEN'],
    dictionaryMap: null,
  },
  {
    columnName: 'statusId',
    caption: 'ИД Статуса',
    filterCaption: 'Статус',
    type: 'Integer',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/dictionary/status',
      keyColumnName: 'statusId',
      valueColumnName: 'statusName',
      searchColumn: null,
    },
  },
  {
    columnName: 'subposition',
    caption: 'Мои подчиненные',
    filterCaption: 'Мои подчиненные',
    type: 'Boolean',
    filterCriteriaList: ['EQ'],
    dictionaryMap: null,
  },
  {
    columnName: 'isPublic',
    caption: 'Публичная',
    filterCaption: 'Публичная',
    type: 'Boolean',
    filterCriteriaList: ['EQ'],
    dictionaryMap: null,
  },
  {
    columnName: 'roomName',
    caption: 'Помещение',
    filterCaption: 'Помещение',
    type: 'String',
    filterCriteriaList: ['EQ'],
    dictionaryMap: null,
  },
];

export const STATUS_REPORT_DEFAULT_COLUMNS: IAvailableColumnModel[] = [
  {
    columnName: 'executorUserName',
    caption: 'Исполнитель заявки',
    type: 'String',
    filterCriteriaList: [],
    dictionaryMap: null,
    filterCaption: 'Исполнитель заявки',
  },
  {
    columnName: 'defectName',
    caption: 'Тип замечания',
    type: 'String',
    filterCriteriaList: [],
    dictionaryMap: null,
    filterCaption: 'Тип замечания',
  },
  {
    columnName: 'total',
    caption: 'Всего заявок',
    type: 'Integer',
    filterCriteriaList: [],
    dictionaryMap: null,
    filterCaption: 'Всего заявок',
  },
];

export const STATUS_REPORT_DEFAULT_FILTERS: IAvailableColumnModel[] = [
  {
    columnName: 'executorId',
    caption: 'ИД исполнителя заявки',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/users/listPerformance',
      keyColumnName: 'username',
      valueColumnName: 'firstLastName',
      searchColumn: 'search',
    },
    filterCaption: 'Исполнитель заявки',
  },
  {
    columnName: 'createUserId',
    caption: 'ИД создателя заявки',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/users/listPerformance',
      keyColumnName: 'username',
      valueColumnName: 'firstLastName',
      searchColumn: 'search',
    },
    filterCaption: 'Создатель заявки',
  },
  {
    columnName: 'doneUserId',
    caption: 'ИД кто выполнил заявку',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/users/listPerformance',
      keyColumnName: 'username',
      valueColumnName: 'firstLastName',
      searchColumn: 'search',
    },
    filterCaption: 'Выполнил заявку',
  },
  {
    columnName: 'closeUserId',
    caption: 'ИД кто закрыл заявки',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/users/listPerformance',
      keyColumnName: 'username',
      valueColumnName: 'firstLastName',
      searchColumn: 'search',
    },
    filterCaption: 'Закрыл заявку',
  },
  {
    columnName: 'createDate',
    caption: 'Дата создания заявки',
    type: 'Date',
    filterCriteriaList: ['BETWEEN'],
    dictionaryMap: null,
    filterCaption: '',
  },
  {
    columnName: 'closeDate',
    caption: 'Дата закрытия заявки',
    type: 'Date',
    filterCriteriaList: ['BETWEEN'],
    dictionaryMap: null,
    filterCaption: 'Дата закрытия заявки',
  },
  {
    columnName: 'doneDate',
    caption: 'Дата выполнения заявки',
    type: 'Date',
    filterCriteriaList: ['BETWEEN'],
    dictionaryMap: null,
    filterCaption: 'Дата выполнения заявки',
  },
  {
    columnName: 'realEstateGuid',
    caption: 'ИД Объекта недвижимости',
    type: 'String',
    filterCriteriaList: ['IN'],
    dictionaryMap: {
      dictionaryUrl: '/dictionary/realEstates',
      keyColumnName: 'guid',
      valueColumnName: 'name',
      searchColumn: 'name',
    },
    filterCaption: 'Объект недвижимости',
  },
];

export const num2str = (number: number, text_forms: string[]) => {
  const n = Math.abs(number) % 100;

  let n1 = n % 10;

  if (n > 10 && n < 20) {
    return `${number} ${text_forms[2]}`;
  }
  if (n1 > 1 && n1 < 5) {
    return `${number} ${text_forms[1]}`;
  }
  if (n1 === 1) {
    return `${number} ${text_forms[0]}`;
  }

  return `${number} ${text_forms[2]}`;
};

export const getUserAvatarPath = (avatar: string | null): string => {
  if (typeof avatar !== 'undefined' && avatar !== null && avatar !== '') {
    return `data:image/png;base64, ${avatar}`;
  }

  return '';
};

export const getUserShortName = (lastName: string, firstName: string): string => {
  let shortName = '';

  if (lastName) {
    shortName = lastName.charAt(0).toUpperCase();
  }

  if (firstName) {
    shortName = `${shortName}${firstName.charAt(0).toUpperCase()}`;
  }

  return shortName;
};

export const downloadFile = (
  file: Blob | null,
  type: string,
  filename: string | null,
  print: boolean = false,
  base64 = '',
): void => {
  const link = document.createElement('a');

  if (!print) {
    const blob = new Blob([file!], { type });

    link.href = URL.createObjectURL(blob);
    link.download = filename ?? 'Unnamed';
  } else {
    link.href = `${type};base64,${base64}`;
    link.target = '_blank';
  }

  link.click();
};

export const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = (error) => reject(error);
  });
};

const getExtention = (file: string) => {
  const imageNameArray = file.split('.');
  return imageNameArray[imageNameArray.length - 1].indexOf('?') > -1
    ? imageNameArray[imageNameArray.length - 1].substring(imageNameArray[imageNameArray.length - 1].indexOf('?'), -1)
    : imageNameArray[imageNameArray.length - 1];
};

export const getIconByExtension = (file: string): React.ReactNode => {
  const extension = getExtention(file).toLowerCase();
  if (['pdf'].indexOf(extension) > -1) {
    return <PdfIcon />;
  } else if (['mp4'].indexOf(extension) > -1) {
    return <VideoFileIcon />;
  } else if (['doc', 'docx'].indexOf(extension) > -1) {
    return <WordFileIcon />;
  } else if (['xlsx', 'xls'].indexOf(extension) > -1) {
    return <ExcelFileIcon />;
  }
  return (
    <InsertDriveFile
      style={{
        color: 'black',
      }}
    />
  );
};

const imageCompressOptions = {
  maxSizeMB: 0.15,
  useWebWorker: true,
  maxWidthOrHeight: 1000,
};

export const compressImage = async (file: File) => {
  if (file.name.split('.').pop() === 'svg') {
    // lib can't compress SVG type files.
    return file;
  }
  try {
    const compressedFile = await imageCompression(file, imageCompressOptions);
    return new File([compressedFile], compressedFile.name, { type: compressedFile.type, lastModified: Date.now() });
  } catch (error) {
    return file;
  }
};

export const isEmail = (email: string) => {
  const emailRe =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  return emailRe.test(email);
};

export const calculateHoursToDays = (totalHours: number) => {
  if (totalHours != null) {
    let days = Math.floor(totalHours / 8);
    let hours = totalHours % 8;
    return `${num2str(days, ['рабочий день', 'рабочих дня', 'рабочих дней'])} 
    ${hours !== 0 ? ' и ' + num2str(Math.round(hours), ['час', 'часа', 'часов']) : ''}`;
  }
};

export const numbersDeclension = (number: number, words: string[]) =>
  words[number % 100 > 4 && number % 100 < 20 ? 2 : [2, 0, 1, 1, 1, 2][number % 10 < 5 ? Math.abs(number) % 10 : 5]];

export const urlify = (text: string) => {
  const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi; // eslint-disable-line no-useless-escape

  return text.replace(urlRegex, (url) => '<a href="' + url + '">' + url + '</a>');
};

export const initials = (initials?: string) =>
  initials
    ?.split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2);

export const getFilenameFromContentDisposition = (contentDisposition: string) => {
  const regex = /filename\*?=(?:(['"])(.*?)\1|([^;\r\n]*))/g;
  const matches = regex.exec(contentDisposition);

  if (matches && matches.length > 1) {
    const filename = matches[2] || matches[3];

    if (filename) return filename;
  }

  return null;
};

export function getTextByValue(value: string | number | boolean | null | undefined) {
  if (!value) return value || '—';

  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';

  if (typeof value === 'number') return value ? Intl.NumberFormat('ru-RU').format(value) : value;

  return value || '—';
}

/**
 * Плюрализация русских существительных по числу: pluralize(2, 'заявка', 'заявки', 'заявок').
 * Общая, а не в модуле страницы, — форма нужна везде, где выводим «N чего-то».
 */
export const pluralize = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
};
